import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Pencil, Plus, RefreshCw, Trash2, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Device, DeviceType } from "@/types";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { CopyableField } from "@/components/ui/CopyableField";

const deviceFormSchema = z.object({ name: z.string().min(1, "Nombre obligatorio") });
type DeviceForm = z.infer<typeof deviceFormSchema>;

// Editor genérico de "productos" (pantallas/tablets) reutilizado en tres sitios: pantallas de
// preview a nivel de club (courtId siempre null) y pantalla de marcador + tablets de árbitro a
// nivel de pista (courtId obligatorio) — el backend valida esa obligatoriedad según el type.
export function DeviceListEditor({
  clubId,
  courtId,
  type,
  title,
  icon: Icon,
  emptyLabel,
}: {
  clubId: number;
  courtId?: number;
  type: DeviceType;
  title: string;
  icon: LucideIcon;
  emptyLabel: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Device | null>(null);
  const [deleting, setDeleting] = useState<Device | null>(null);
  const [regenerating, setRegenerating] = useState<Device | null>(null);
  const queryKey = ["devices", type, clubId, courtId ?? null];

  const { data: devices, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ clubId: String(clubId), type });
      if (courtId) params.set("courtId", String(courtId));
      return (await api.get<Device[]>(`/devices?${params.toString()}`)).data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DeviceForm>({ resolver: zodResolver(deviceFormSchema) });

  function startCreate() {
    setEditing(null);
    reset({ name: "" });
  }

  function startEdit(device: Device) {
    setEditing(device);
    reset({ name: device.name });
  }

  const save = useMutation({
    mutationFn: (data: DeviceForm): Promise<unknown> =>
      editing
        ? api.put(`/devices/${editing.id}`, data)
        : api.post("/devices", { ...data, clubId, courtId: courtId ?? null, type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(editing ? "Dispositivo actualizado" : "Dispositivo añadido");
      setEditing(null);
      reset({ name: "" });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo guardar el dispositivo"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Dispositivo eliminado");
    },
    onError: () => toast.error("No se pudo eliminar el dispositivo"),
  });

  const regenerateToken = useMutation({
    mutationFn: (id: number) => api.post(`/devices/${id}/regenerate-token`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Nueva URL generada — la anterior ha dejado de funcionar");
      setRegenerating(null);
    },
    onError: () => toast.error("No se pudo regenerar la URL"),
  });

  const inputId = `device-name-${type}-${clubId}-${courtId ?? "club"}`;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-ink-100 p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
        <Icon className="h-4 w-4 text-brand-600" />
        {title}
      </p>

      {isLoading ? (
        <p className="text-sm text-ink-400">Cargando…</p>
      ) : !devices?.length ? (
        <p className="text-sm text-ink-400">{emptyLabel}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {devices.map((device) => (
            <li key={device.id} className="flex flex-col gap-2 rounded-lg border border-ink-100 px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-sm font-medium text-ink-700">{device.name}</span>
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" onClick={() => startEdit(device)} title="Editar">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" onClick={() => setDeleting(device)} title="Eliminar">
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
              {device.viewUrl && (
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <CopyableField value={device.viewUrl} />
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setRegenerating(device)}
                    disabled={regenerateToken.isPending}
                    title="Generar una URL nueva (invalida la anterior)"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit((data) => save.mutate(data))} className="flex items-end gap-2">
        <div className="flex-1">
          <Input id={inputId} label={editing ? "Editar nombre" : "Nombre"} error={errors.name?.message} {...register("name")} />
        </div>
        {editing && (
          <Button type="button" onClick={startCreate}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={save.isPending}>
          <Plus className="h-4 w-4" />
          {editing ? "Guardar" : "Añadir"}
        </Button>
      </form>

      {deleting && (
        <ConfirmDialog
          open={!!deleting}
          onOpenChange={(open) => !open && setDeleting(null)}
          title="Eliminar dispositivo"
          description={`¿Seguro que quieres eliminar "${deleting.name}"?`}
          tone="danger"
          confirmLabel="Eliminar"
          onConfirm={() => remove.mutate(deleting.id)}
        />
      )}

      {regenerating && (
        <ConfirmDialog
          open={!!regenerating}
          onOpenChange={(open) => !open && setRegenerating(null)}
          title="Regenerar URL"
          description={`¿Seguro que quieres generar una URL nueva para "${regenerating.name}"? La URL actual dejará de funcionar de inmediato.`}
          tone="primary"
          confirmLabel="Regenerar"
          onConfirm={() => regenerateToken.mutate(regenerating.id)}
        />
      )}
    </div>
  );
}
